import './module-template-page.css';

import classNames from 'classnames';
import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import ReactPlayer from 'react-player';

import { AlmaLink } from '../../components/alma-link';
import { BlogPortableText } from '../../components/blog-portable-text/blog-portable-text';
import { Cards } from '../../components/cards';
import { GoldcastForm } from '../../components/goldcast-form';
import { GoldcastVideoEmbed } from '../../components/goldcast-video-embed';
import { MarketoForm } from '../../components/marketo-form';
import { SEO } from '../../components/seo';
import { Stacks } from '../../components/stacks';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getCookie } from '../../utils/cookie-helpers';
import * as styles from './webinar-layout.module.css';

// TODO: move all these types to component level and export from there
interface DynamicContent {
	_key: string;
	_type: string;
}

interface Expert {
	_key: string;
	_type: string;
	arrowPosition?: string;
	backgroundColor?: string;
	buttonColorTheme?: string;
	buttonFillBorder?: string;
	cardColor?: string;
	cardPadding?: {
		_key: string;
		_type: string;
		paddingHorizontal: number;
		paddingVertical: number;
	};
	cardTitleStyle?: string;
	cards?: Card[];
	cardsPerRow?: number;
	contentOrder?: string;
	hasArrow?: boolean;
	imageAspectRatio?: string;
	kind?: string;
	linkColor?: string;
	textColor?: string;
	twoCardColumnRatio?: string;
	underlined?: boolean;
	useSliderForMobile?: boolean;
	verticalPadding?: {
		modulePaddingBottom: number;
		modulePaddingTop: number;
	};
}

interface Card {
	_id: string;
	_key: string;
	_type: string;
	ctaLink?: string;
	ctaTitle?: string;
	iconSvg?: {
		_type: string;
		icon: {
			_id: string;
			_type: string;
			iconSvg: string;
		};
		iconColor?: string;
		iconSize?: string;
		iconStyle?: string;
	};
	image?: {
		asset: {
			gatsbyImageData: unknown;
		};
	};
	link?: string;
	_rawDescription?: unknown;
	socialLinks?: {
		_key: string;
		_type: string;
		icon: string;
		link: string;
	}[];
	subtitle?: string;
	title: string;
}

interface Resource {
	ctaTitle?: string;
	description?: string;
	resourceDynamicContent?: DynamicContent[];
	enableRecaptcha?: boolean;
	eventDate?: string;
	experts?: Expert[];
	resourceFormCTAText?: string;
	resourceFormHeaderText?: string;
	resourceFormThankYouMessage?: string;
	formType?: string;
	goldcastEventType: string;
	goldcastFormEventId?: string;
	goldcastFormRegistrationUrl?: string;
	goldcastFormSeriesId?: string;
	goldcastVideoId?: string;
	hasDynamicContent?: boolean;
	image?: {
		asset: {
			gatsbyImageData: unknown;
		};
	};
	openGraph?: {
		description?: string;
		title?: string;
		image?: {
			asset?: {
				url?: string;
			};
		};
	};
	recordedOn?: string;
	relatedResources?: {
		_id: string;
		image?: {
			asset?: {
				gatsbyImageData: unknown;
			};
		};
		pageType?: string;
		resourceType?: {
			_id: string;
			title: string;
		}[];
		slug?: {
			current: string;
		};
		title: string;
	}[];
	_rawResourceContent?: {
		_type: string;
		_key: string;
		children: {
			_type: string;
			_key: string;
			text: string;
		}[];
	}[];
	resourceForm?: {
		formId?: string;
	};
	resourceType?: {
		title?: string;
	}[];
	tags?: {
		_id: string;
		slug: {
			current: string;
		};
		title: string;
	}[];
	title?: string;
	topics?: {
		slug?: {
			current: string;
		};
		title?: string;
	}[];
	video?: {
		url: string;
	};
	videoType?: string;
}

interface SiteSettings {
	description?: string;
	openGraph?: {
		title?: string;
		image?: {
			asset?: {
				url?: string;
			};
		};
	};
	keywords?: string[];
}

interface ResourceGatedForm {
	formId?: string;
}

interface WebinarLayoutProps {
	upcomingEvent?: boolean;
	resource: Resource;
	resourceGatedForm?: ResourceGatedForm;
	siteSettings: SiteSettings;
}

function WebinarLayout({
	upcomingEvent,
	resource,
	resourceGatedForm,
	siteSettings,
}: WebinarLayoutProps) {
	// hooks
	const isMobile = useMediaQuery('(max-width: 799px)');

	// set up resource data
	const ctaLink = resourceGatedForm?.formId;
	const ctaTitle = resource?.ctaTitle;
	const description = resource?.description;
	const dynamicContent = resource?.resourceDynamicContent;
	const enableRecaptcha = resource?.enableRecaptcha;
	const eventDate = resource?.eventDate;
	const experts = resource?.experts?.[0];
	const formCTAText = resource?.resourceFormCTAText;
	const formHeaderText = resource?.resourceFormHeaderText;
	const formThankYouMessage = resource?.resourceFormThankYouMessage;
	const formType = resource?.formType;
	const goldcastEventType = resource?.goldcastEventType;
	const goldcastFormEventId = resource?.goldcastFormEventId;
	const goldcastFormRegistrationUrl = resource?.goldcastFormRegistrationUrl;
	const goldcastFormSeriesId = resource?.goldcastFormSeriesId;
	const goldcastVideoId = resource?.goldcastVideoId;
	const hasDynamicContent = resource?.hasDynamicContent;
	const image = resource?.image?.asset
		? getImage(resource.image.asset)
		: null;
	const openGraph = resource?.openGraph;
	const recordedOn = resource?.recordedOn;
	const relatedResources = resource?.relatedResources;
	const resourceContent = resource?._rawResourceContent;
	const resourceMarketoForm = resource?.resourceForm;
	const resourceType = resource?.resourceType?.[0]?.title;
	const tags = resource?.tags;
	const title = resource?.title;
	const topic = resource?.topics?.[0];
	const upcomingCtaLink = resourceMarketoForm?.formId;
	const video = resource?.video;
	const videoType = resource?.videoType;

	// seo data
	const pageDescription = openGraph?.description ?? siteSettings.description;
	const pageTitle = openGraph?.title ?? siteSettings.openGraph?.title ?? '';
	const seoImageUrl =
		openGraph?.image?.asset?.url ??
		siteSettings.openGraph?.image?.asset?.url ??
		'';

	const resourceHubCookie = getCookie('resource_hub_form_submitted');

	const isMarketoForm = formType === 'marketoForm';
	const isGoldcastForm = formType === 'goldcastForm';

	// video types
	const isGoldcastVideo = videoType === 'goldcastVideo';
	const isExternalVideo = videoType === 'externalVideo';

	return (
		<div>
			<SEO
				description={pageDescription}
				imageUrl={seoImageUrl}
				keywords={siteSettings.keywords}
				title={pageTitle}
			/>
			<div
				className={classNames(
					'manual-header-spacer',
					'responsive-layout',
					'resource-template',
					styles.moduleContainer
				)}
			>
				{/* hero */}
				<div
					className={classNames(
						'full-width-container',
						'hero-container'
					)}
				>
					<div
						className={classNames(
							'content-container',
							styles.heroLayoutContainer
						)}
					>
						{topic && (
							<p className={isMobile ? 'body-m' : 'body-xl'}>
								<strong>
									<Link to='/for-providers/resources/'>
										Provider Resources
									</Link>
									<span className='topic-breadcrumb-arrow'>
										{` › `}
									</span>
									<Link
										to={`/for-providers/resources/topics/${topic?.slug?.current}`}
									>
										<span className='topic-breadcrumb'>
											{topic?.title}
										</span>
									</Link>
								</strong>
							</p>
						)}
						<div
							className={classNames(
								styles.heroContentContainer,
								'hero-content-container'
							)}
						>
							<div className='hero-content'>
								{resourceType && (
									<p
										className={classNames(
											'heading-xs',
											styles.contentType
										)}
									>
										{resourceType}
									</p>
								)}
								{title && (
									<h1
										className={classNames(
											'heading-xl',
											styles.contentTitle
										)}
									>
										{title}
									</h1>
								)}
								{description && (
									<p
										className={classNames(
											isMobile ? 'body-l' : 'body-xl',
											styles.contentDescription
										)}
									>
										{description}
									</p>
								)}
								{!recordedOn && eventDate && (
									<p
										className={classNames(
											'body-display',
											styles.eventDate
										)}
									>
										{eventDate}
									</p>
								)}
								{ctaTitle &&
									(ctaLink ?? upcomingCtaLink) &&
									(upcomingEvent ?? !resourceHubCookie) &&
									isMarketoForm && (
										<div className={styles.contentCTA}>
											<AlmaLink
												cta={{
													buttonColorTheme: 'dark',
													kind: 'button',
													title: ctaTitle,
													route: `#mktoForm_${upcomingEvent ? upcomingCtaLink : ctaLink}`,
												}}
											/>
										</div>
									)}
								{ctaTitle &&
									(upcomingEvent ?? !resourceHubCookie) &&
									isGoldcastForm && (
										<div className={styles.contentCTA}>
											<AlmaLink
												cta={{
													buttonColorTheme: 'dark',
													kind: 'button',
													title: ctaTitle,
													route: `#goldcast-registration-form`,
												}}
											/>
										</div>
									)}
								{recordedOn && <p>Recorded on {recordedOn}</p>}
							</div>
							<div>
								{image && (
									<GatsbyImage
										image={image}
										alt={title ?? 'Resource Image'}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
				{/* content */}
				<div
					className={classNames(
						'full-width-container',
						'content-container-outer'
					)}
				>
					<div
						className={classNames(
							'content-container',
							'content-layout'
						)}
					>
						{resourceHubCookie && isExternalVideo && video && (
							<div className='col col-full'>
								<ReactPlayer
									className={`react-player ${styles.reactPlayer}`}
									controls
									width='100%'
									height='auto'
									url={video.url}
								/>
							</div>
						)}
						{isGoldcastVideo && goldcastVideoId && (
							<div className='col col-full'>
								<GoldcastVideoEmbed videoId={goldcastVideoId} />
							</div>
						)}
						<div className='col col-l-6'>
							{!isGoldcastVideo &&
								!goldcastVideoId &&
								resourceContent != null && (
									<div className='description-wrapper'>
										<BlogPortableText
											blocks={
												resource._rawResourceContent
											}
										/>
									</div>
								)}
							{tags && (
								<div className='tags-container'>
									<p className='body-m tags-title'>Tags:</p>
									<ul className='tags-list'>
										{tags.map((tag) => {
											const { _id, slug, title } = tag;

											return (
												<Link
													key={_id}
													to={`/for-providers/resources/tags/${slug.current}`}
												>
													<li className='tag'>
														<p className='body-s'>
															{title}
														</p>
													</li>
												</Link>
											);
										})}
									</ul>
								</div>
							)}
						</div>

						{!isGoldcastVideo && !goldcastVideoId && (
							<div className='col col-r-5'>
								{isMarketoForm &&
									(resourceMarketoForm ??
										resourceGatedForm) &&
									(upcomingEvent ?? !resourceHubCookie) && (
										<MarketoForm
											enableRecaptcha={enableRecaptcha}
											columnLayout={'hybridcol'}
											formCtaCopy={formCTAText}
											headerTextOverride={formHeaderText}
											// if its an upcoming event we want to use the form unique to the event
											// otherwise we will use the default gated form
											marketoForm={
												upcomingEvent
													? resourceMarketoForm
													: resourceGatedForm
											}
											resourceLayout
											setResourceFormCookie={true}
											thankYouMessageOverride={
												formThankYouMessage
											}
										/>
									)}
								{isGoldcastForm && (
									<div
										className={classNames(
											'bcpgr',
											styles.goldcastFormWrapper
										)}
									>
										{formHeaderText && (
											<div
												className={
													styles.goldcastHeaderContainer
												}
											>
												<h2 className='heading-l'>
													{formHeaderText}
												</h2>
											</div>
										)}
										<GoldcastForm
											eventType={goldcastEventType}
											eventId={goldcastFormEventId}
											seriesId={goldcastFormSeriesId}
											registrationUrl={
												goldcastFormRegistrationUrl
											}
										/>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
				{/* speakers */}
				{experts && (
					<div
						className={classNames(
							'full-width-container',
							'content-container-outer'
						)}
					>
						<div
							className={classNames(
								'content-container',
								styles.speakersLayout
							)}
						>
							<h2
								className={classNames(
									'heading-xl',
									styles.expertsTitle
								)}
							>
								Meet our experts
							</h2>
							<Cards {...experts} />
						</div>
					</div>
				)}
				{/* dynamic content */}
				{hasDynamicContent && (
					<div
						className={classNames(
							'dynamicContentContainer',
							'full-width-container'
						)}
					>
						{dynamicContent?.map((content) => {
							const { _type } = content;

							switch (_type) {
								case 'stacks':
									return (
										<Stacks
											key={content._key}
											{...content}
										/>
									);
								case 'cards':
									return (
										<Cards
											key={content._key}
											{...content}
										/>
									);
								default:
									return null;
							}
						})}
					</div>
				)}
				{/* related resources */}
				{recordedOn &&
					relatedResources &&
					relatedResources.length > 0 && (
						<div
							className={classNames(
								'full-width-container',
								'relatedResourcesContainer'
							)}
						>
							<div
								className={classNames(
									'content-container',
									'relatedResourcesLayout'
								)}
							>
								<h2 className='heading-l'>Related resources</h2>
								<div className='relatedResources'>
									{relatedResources?.map(
										(relatedResource) => {
											const {
												_id,
												image,
												pageType,
												resourceType,
												slug,
												title,
											} = relatedResource;

											const resourceTypeTitle =
												resourceType?.[0]?.title;
											const relatedResourceImage =
												getImage(image?.asset ?? null);

											// set up resource url
											const isBlogPost =
												pageType === 'blogPost';

											const blogUrl = `/blog/${slug?.current}`;
											const resourceUrl = `/for-providers/resources/${slug?.current}`;

											const resourceLink = isBlogPost
												? blogUrl
												: resourceUrl;

											return (
												<a
													key={_id}
													href={resourceLink}
												>
													<div className='relatedResource'>
														{relatedResourceImage && (
															<GatsbyImage
																alt={
																	title ||
																	'Related Resource Image'
																}
																image={
																	relatedResourceImage
																}
															/>
														)}
														<div className='relatedResourceContent'>
															<p className='body-sm-caps'>
																{
																	resourceTypeTitle
																}
															</p>
															<p className='body-display-sm'>
																{title}
															</p>
														</div>
													</div>
												</a>
											);
										}
									)}
								</div>
							</div>
						</div>
					)}
			</div>
		</div>
	);
}

export default WebinarLayout;
